import { Link } from 'react-router-dom';
import { BRAND_MARK_URL, BRAND_LOCKUP_URL } from '@/lib/brand';

type LogoProps = {
  /**
   * lockup   = the full horizontal logo image (G emblem + GREENSIDE + NEWS) as ONE asset.
   * wordmark = the emblem mark plus live text (used in the footer).
   */
  variant?: 'lockup' | 'wordmark';
  /** Color treatment for the wordmark variant: onDark = light text, onLight = dark text. */
  tone?: 'onDark' | 'onLight';
  /** Render the G emblem as a solid white knockout (best on dark green surfaces). */
  whiteMark?: boolean;
  /** Hide the wordmark on the smallest screens, leaving just the G emblem. */
  collapseOnMobile?: boolean;
  /** Optional link target. Pass null to render a non-link element. */
  to?: string | null;
  className?: string;
};

const LOCKUP_SIZES = 'block h-auto w-[160px] sm:w-[200px] lg:w-[240px] object-contain object-left';

export default function Logo({
  variant = 'wordmark',
  tone = 'onLight',
  whiteMark = false,
  collapseOnMobile = true,
  to = '/',
  className = '',
}: LogoProps) {
  const heading = tone === 'onDark' ? 'text-background-50' : 'text-foreground-950';
  const sub = tone === 'onDark' ? 'text-accent-400' : 'text-primary-500';
  const wordmark = collapseOnMobile ? 'hidden sm:flex' : 'flex';

  const inner = variant === 'lockup' ? (
    <img
      src={BRAND_LOCKUP_URL}
      alt="GreenSide News logo"
      title="GreenSide News"
      className={LOCKUP_SIZES}
    />
  ) : (
    <>
      <img
        src={BRAND_MARK_URL}
        alt="GreenSide News"
        title="GreenSide News"
        className={`h-10 w-10 md:h-12 md:w-12 object-contain object-top ${whiteMark ? 'brightness-0 invert' : ''}`}
      />
      <span className={`flex-col leading-none ${wordmark}`}>
        <span className={`font-heading font-extrabold uppercase tracking-tight text-2xl md:text-3xl ${heading}`}>
          GREENSIDE
        </span>
        <span className={`font-label font-bold uppercase tracking-[0.34em] text-[11px] md:text-xs mt-1 ${sub}`}>
          NEWS
        </span>
      </span>
    </>
  );

  if (to === null) {
    return <div className={`flex items-center ${className}`}>{inner}</div>;
  }

  return (
    <Link to={to} className={`flex items-center cursor-pointer shrink-0 ${className}`}>
      {inner}
    </Link>
  );
}